import { ReactNode } from "react";
import styles from "./AnalyticsTable.module.scss";
import AnalyticsCard from "../card/AnalyticsCard";
import AnalyticsHeader from "../header/AnalyticsHeader";
import AnalyticsState from "../state/AnalyticsState";

export interface AnalyticsTableColumn<T> {
	header: string;
	/** Figures read better against their last digit; text against its first. */
	align?: "left" | "right";
	/** The cell's whole content, so a column can hold a badge as easily as a number. */
	render: (row: T) => ReactNode;
}

type Props<T> = {
	title?: string;
	subtitle?: string;
	/** The rows are stand-ins rather than measurements — see AnalyticsHeader. */
	mock?: boolean;
	columns: AnalyticsTableColumn<T>[];
	/** Drawn in the order given — the component does not sort. */
	rows: T[];
	/** Kept in the body rather than handed to AnalyticsState, so the columns stay named. */
	emptyMessage?: string;
	/** Both default to false, for a table built from data that is already in hand. */
	loading?: boolean;
	error?: boolean;
	/** The query's refetch and whether one is already running — see AnalyticsState. */
	onRetry?: () => void;
	retrying?: boolean;
};

/** Rows of figures under a heading, in the same card the other widgets sit in. */
function AnalyticsTable<T>({
	title,
	subtitle,
	mock = false,
	columns,
	rows,
	emptyMessage = "No data for this selection.",
	loading = false,
	error = false,
	onRetry,
	retrying,
}: Readonly<Props<T>>) {
	return (
		<AnalyticsCard>
			{title && <AnalyticsHeader title={title} subtitle={subtitle} mock={mock} />}
			<AnalyticsState loading={loading} error={error} onRetry={onRetry} retrying={retrying}>
				<div className={styles.scroller}>
					<table className={styles.table}>
						<thead>
							<tr>
								{columns.map(column => (
									<th key={column.header} className={`${styles.header} ${styles[column.align ?? "left"]}`}>
										{column.header}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{rows.length === 0 ? (
								<tr>
									<td className={styles.empty} colSpan={columns.length}>
										{emptyMessage}
									</td>
								</tr>
							) : (
								rows.map((row, index) => (
									<tr key={index}>
										{columns.map(column => (
											<td key={column.header} className={`${styles.cell} ${styles[column.align ?? "left"]}`}>
												{column.render(row)}
											</td>
										))}
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</AnalyticsState>
		</AnalyticsCard>
	);
}

export default AnalyticsTable;
